// src/components/WorkOrderForm.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// Import MUI
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
  TextField,
  Autocomplete,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

function WorkOrderForm({ teamId, userRole }) {
  // --- MODIFICA: Inizializza t() ---
  const { t } = useTranslation('workshop');
  // --- FINE MODIFICA ---
  
  const navigate = useNavigate();
  const { workOrderId } = useParams();
  const isEditing = Boolean(workOrderId);

  // Stati del Form (invariati)
  const [title, setTitle] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Stati per caricamento (invariati)
  const [allVehicles, setAllVehicles] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isManager = userRole === 'admin' || userRole === 'Engineer';

  // Carica i veicoli (invariato)
  useEffect(() => {
    if (!teamId) { setIsLoadingVehicles(false); return; }
    setIsLoadingVehicles(true);
    
    const q = query(
      collection(db, "vehicles"),
      where("teamId", "==", teamId),
      orderBy("marca")
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const vehicleList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllVehicles(vehicleList);
      setIsLoadingVehicles(false);
    }, (error) => {
      console.error("Error loading vehicles:", error);
      setIsLoadingVehicles(false);
    });

    return () => unsubscribe();
  }, [teamId]);

  // Carica gli eventi
  useEffect(() => {
    if (!teamId) { setIsLoadingEvents(false); return; }
    setIsLoadingEvents(true);
    
    const q = query(
      collection(db, "events"),
      where("teamId", "==", teamId),
      orderBy("startDate", "desc")
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const eventList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllEvents(eventList);
      setIsLoadingEvents(false);
    }, (error) => {
      console.error("Error loading events:", error);
      setIsLoadingEvents(false);
    });

    return () => unsubscribe();
  }, [teamId]);
  
  // useEffect per caricare i dati in modalità Modifica (invariato)
  useEffect(() => {
    if (isEditing && allVehicles.length > 0 && !isLoadingEvents) {
      setIsLoadingData(true);
      const workOrderRef = doc(db, "workOrders", workOrderId);
      getDoc(workOrderRef).then(docSnap => {
        if (docSnap.exists() && docSnap.data().teamId === teamId) {
          const data = docSnap.data();
          setTitle(data.title);
          const vehicle = allVehicles.find(v => v.id === data.vehicleId);
          if (vehicle) {
            setSelectedVehicle(vehicle);
          }
          if (data.eventId && allEvents.length > 0) {
            const ev = allEvents.find(e => e.id === data.eventId);
            if (ev) setSelectedEvent(ev);
          }
        } else {
          console.error("Work Order not found or access denied.");
          navigate('/workshop');
        }
        setIsLoadingData(false);
      });
    }
  }, [isEditing, workOrderId, allVehicles, allEvents, isLoadingEvents, teamId, navigate]);
  
  // --- MODIFICA: handleSaveWorkOrder (con t() per alert) ---
  const handleSaveWorkOrder = async (e) => {
    e.preventDefault();
    if (!title || !selectedVehicle) {
      alert(t('form.error_required')); // Tradotto
      return;
    }
    
    setIsSubmitting(true);
    
    const data = {
      teamId: teamId,
      title: title,
      vehicleId: selectedVehicle.id,
      vehicleName: `${selectedVehicle.marca} ${selectedVehicle.modello}`,
      eventId: selectedEvent ? selectedEvent.id : null,
      eventName: selectedEvent ? selectedEvent.eventName : null,
    };

    try {
      if (isEditing) {
        const workOrderRef = doc(db, "workOrders", workOrderId);
        await updateDoc(workOrderRef, data);
        navigate(`/workshop/${workOrderId}`);
        
      } else {
        data.status = "Open";
        data.createdAt = serverTimestamp();
        data.createdBy = userRole;
        
        const docRef = await addDoc(collection(db, "workOrders"), data);
        navigate(`/workshop/${docRef.id}`);
      }
      
    } catch (error) {
      console.error("Error saving work order:", error);
      alert(t('form.error_save', { message: error.message })); // Tradotto
      setIsSubmitting(false);
    }
  };
  
  // --- MODIFICA: Sezione Access Denied (con t()) ---
  if (!isManager) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">{t('form.access_denied_title')}</Typography>
        <Typography>{t('form.access_denied_subtitle')}</Typography>
        <Button variant="contained" onClick={() => navigate('/workshop')} sx={{ mt: 2 }}>
          {t('form.back_to_workshop')}
        </Button>
      </Paper>
    );
  }
  // --- FINE MODIFICA ---

  if (isLoadingData) {
    return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 10 }} />;
  }

  return (
    <>
      {/* 1. Header (con t()) */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'background.paper', 
          border: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            color="inherit" 
            onClick={() => navigate(isEditing ? `/workshop/${workOrderId}` : '/workshop')}
            sx={{ mr: 2, p: 0.5 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {isEditing ? t('form.edit_title') : t('form.new_title')} {/* Tradotto */}
          </Typography>
        </Box>
        <Button
          color="primary"
          variant="contained"
          disabled={isSubmitting || isLoadingVehicles}
          onClick={handleSaveWorkOrder}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
          size="large"
          sx={{ height: '56px' }}
        >
          {isEditing ? t('form.update_button') : t('form.save_button')} {/* Tradotto */}
        </Button>
      </Paper>

      {/* 2. Form (con t()) */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 },
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Box component="form" onSubmit={handleSaveWorkOrder}>
          <Typography variant="h6" gutterBottom>{t('form.details_title')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
            <TextField
              label={t('form.title_label')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
              helperText={t('form.title_helper')}
            />
            <Autocomplete
              options={allVehicles}
              getOptionLabel={(option) => `${option.marca} ${option.modello} (Nr. ${option.numeroGara || 'N/A'})`}
              value={selectedVehicle}
              onChange={(event, newValue) => {
                setSelectedVehicle(newValue);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isLoadingVehicles}
              disabled={isLoadingVehicles || isEditing}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('form.vehicle_label')}
                  required
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingVehicles ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              options={allEvents}
              getOptionLabel={(option) => option.eventName || 'Evento senza nome'}
              value={selectedEvent}
              onChange={(event, newValue) => {
                setSelectedEvent(newValue);
              }}
              isOptionEqualToValue={(option, value) => (value ? option.id === value.id : false)}
              loading={isLoadingEvents}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('form.event_label', 'Evento collegato (Opzionale)')}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingEvents ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </Paper>
    </>
  );
}

export default WorkOrderForm;